import {
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import PropTypes from "prop-types";
import { useMemo, useState } from "react";

import { noPropagationHandler } from "@lib/app.helpers";

import "./context-menu.scss";

export const ContextMenu = ({ icon, children, className }) => {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, placement, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: "bottom-start",
    middleware: [offset(0), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  const dismiss = useDismiss(context);
  const click = useClick(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([
    dismiss,
    click,
  ]);

  const initialTop = useMemo(
    () => (placement == "top" ? 10 : -10),
    [placement],
  );

  return (
    <div className={clsx("context-menu", className)}>
      <button
        ref={refs.setReference}
        className="context-menu-button"
        {...getReferenceProps({
          // Workaround to prevent the context menu event from bubbling up to the parent
          onClick: noPropagationHandler(),
        })}
      >
        {icon ?? <FontAwesomeIcon icon={["fa-regular", "ellipsis-vertical"]} />}
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="context-menu-options"
            initial={{ opacity: 0, top: initialTop }}
            animate={{ opacity: 1, top: 0 }}
            exit={{ opacity: 0, top: initialTop }}
            ref={refs.setFloating}
            style={{ ...floatingStyles }}
            {...getFloatingProps({
              onClick: noPropagationHandler(() => setIsOpen((v) => !v)),
            })}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

ContextMenu.propTypes = {
  icon: PropTypes.node,
  children: PropTypes.node,
  className: PropTypes.string,
};
